import { useState, useRef, useCallback, TouchEvent, MouseEvent } from 'react';

const MIN_SWIPE_DISTANCE = 100;
export const useSwiper = (dataLength: number) => {
  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const [isAtStart, setIsAtStart] = useState<boolean>(true);
  const [isAtEnd, setIsAtEnd] = useState<boolean>(false);
  const [visibleIndex, setVisibleIndex] = useState<number>(0);
  const swiperRef = useRef<HTMLDivElement | null>(null);
  const [startX, setStartX] = useState<number | null>(null);
  const [endX, setEndX] = useState<number | null>(null);

  const checkForOverflow = useCallback(() => {
    if (swiperRef.current) {
      setIsOverflow(
        swiperRef.current.getBoundingClientRect().width <
          swiperRef.current.scrollWidth
      );
      const targetScrollLeft =
        swiperRef.current.getBoundingClientRect().width * visibleIndex;
      swiperRef.current.scrollTo({
        left: targetScrollLeft,
        behavior: 'auto',
      });
    }
  }, [visibleIndex]);

  const updateArrowStates = useCallback(() => {
    if (swiperRef.current) {
      setIsAtStart(visibleIndex === 0);
      setIsAtEnd(visibleIndex === dataLength - 1);
    }
  }, [visibleIndex, dataLength]);

  const listScroll = useCallback((index: number) => {
    if (swiperRef.current) {
      const targetScrollLeft =
        swiperRef.current.getBoundingClientRect().width * index;
      swiperRef.current.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleNextClick = useCallback(() => {
    if (swiperRef.current) {
      listScroll(Math.min(visibleIndex + 1, dataLength - 1));
      setVisibleIndex(prevState => Math.min(prevState + 1, dataLength - 1));
    }
  }, [visibleIndex, dataLength, listScroll]);

  const handlePrevClick = useCallback(() => {
    if (swiperRef.current) {
      setVisibleIndex(prevState => Math.max(prevState - 1, 0));
      listScroll(Math.max(visibleIndex - 1, 0));
    }
  }, [visibleIndex, listScroll]);

  const onStart = useCallback((clientX: number) => {
    setEndX(null);
    setStartX(clientX);
  }, []);

  const onMove = useCallback((clientX: number) => {
    setEndX(clientX);
  }, []);

  const onEnd = useCallback(() => {
    if (!startX || !endX) return;
    const distance = startX - endX;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;
    if (isLeftSwipe) {
      handleNextClick();
    }
    if (isRightSwipe) {
      handlePrevClick();
    }
  }, [endX, handleNextClick, handlePrevClick, startX]);

  const onTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      onStart(e.targetTouches[0].clientX);
    },
    [onStart]
  );

  const onTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      onMove(e.targetTouches[0].clientX);
    },
    [onMove]
  );

  const onMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onStart(e.clientX);
    },
    [onStart]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (startX !== null) {
        onMove(e.clientX);
      }
    },
    [startX, onMove]
  );

  const onMouseUp = useCallback(() => {
    onEnd();
    setStartX(null);
  }, [onEnd]);

  const onMouseLeave = useCallback(() => {
    if (startX !== null) {
      onEnd();
      setStartX(null);
    }
  }, [startX, onEnd]);

  const barElementClick = useCallback(
    (index: number) => {
      if (swiperRef.current) {
        setVisibleIndex(index);
        listScroll(index);
      }
    },
    [listScroll]
  );
  return {
    isOverflow,
    isAtStart,
    isAtEnd,
    visibleIndex,
    swiperRef,
    checkForOverflow,
    updateArrowStates,
    onTouchStart,
    onTouchMove,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    handleNextClick,
    handlePrevClick,
    barElementClick,
  };
};
